<script setup lang="ts">
import type { BoothData } from '@/types/booth'
import { BoothColors } from '~/lib/colors'

import { useLogEvent } from "~/lib/firebase"
const logEvent = useLogEvent()

interface Props {
  modelValue: boolean
  data: BoothData | undefined
  index: number | undefined;
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>()

const model = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

watchEffect(() => {
  if (props.data && props.index !== undefined) {
    logEvent("view_modal", {
      name: "booth",
      booth_org: props.data["祭典名"],
      booth_id: props.data["ID"],
      booth_index: props.index + 1,
    })
  }
})

const mapRef = ref(false)

const maps = Object.keys(import.meta.glob("~/public/map/*.jpg")).map(v =>v.match( /[^\/]+(?=\.jpg)/gm)?.[0]!)
function getMaps(data: BoothData) {
  if (/エリア/.test(data["建物名"])) {
    return [`/2023/map/${data['建物名'].replace(/\s/gm, '')}.jpg`]
  } else if (maps.includes(`${data['祭典名']}_${data['建物名'].replace(/\s/gm, '')}`)) {
    return [`/2023/map/${data['祭典名']}_${data['建物名'].replace(/\s/gm, '')}.jpg`]
  } else {
    return []
  }  
}

function getBoothCategoryInitial(category: string) {
  return { "その他": "他" }[category] ?? category.slice(0, 1)
}
</script>

<template>
  <input type="checkbox" id="my_modal_7" class="modal-toggle" v-model="model" />
  <div v-if="props.data" class="modal">
    <div class="modal-box px-4 w-[calc(100vw-16px)] md:px-8">
      <label class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2" for="my_modal_7">
        ✕
      </label>
      <div class=" p-0 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0">
        <div class="row-span-2 grid place-items-center">
          <div
            class="w-[60px] h-[60px] grid place-items-center bg-primary rounded text-white text-4xl"
            :style="{ 'background-color': BoothColors[props.data['祭典名']]}"
          >
            {{ getBoothCategoryInitial(props.data["分類"]) }}
          </div>
        </div>
        <h3 class="text-md flex flex-wrap justify-center gap-x-2 text-center">
          <span v-for="text in props.data['団体名'].split(';')">
            {{ text }}
          </span>
        </h3>
        <h2 class="text-2xl flex flex-wrap justify-center gap-x-2 text-center">
          <span v-for="text in props.data['企画名'].split(';')">
            {{ text }}
          </span>
        </h2>
      </div>
      <div class="col-span-3 divider my-0" />
      <div class="mb-4">
        {{ props.data["概要"] }}
      </div>
      <div class="p-0 grid grid-cols-[auto_1fr_auto] gap-y-4 gap-x-0">
        <div>
          <div class="text-lg whitespace-nowrap bg-gray-500 text-white rounded px-1.5 py-0.5">場所</div>
        </div>
        <div class="ml-2 text-lg mt-0.5 flex flex-wrap gap-x-2">
          <span v-for="text in props.data['建物名'].split(';')">
            {{ text }}
          </span>
          <span v-for="text in props.data['部屋・フロア'].split(';')">
            {{ text }}
          </span>
        </div>
        <div class=" flex h-[32px] items-center overflow-visible">
          <button 
            v-if="0 < getMaps(props.data).length"
            class="text-lg whitespace-nowrap bg-primary text-white rounded-full h-[40px] w-[40px] pt-[1px] pl-[2px] shadow-lg"
            @click="() => mapRef = !mapRef"
          >
            <i class="mdi mdi-map-search text-[26px]" ></i>
          </button>
        </div>
        <div class="col-[2/4] collapse rounded-none">
          <input type="checkbox" class="hidden" v-model="mapRef" /> 
          <div class="collapse-content mr-l pl-0 pr-0 pb-0 relative max-h-[40vh] overflow-y-scroll"> 
            <NuxtLink v-for="src in getMaps(props.data)" :to="src" target="_blank">
              <img :src="src" />
            </NuxtLink>
          </div>
        </div>
        <div>
          <div class="text-lg whitespace-nowrap bg-gray-500 text-white rounded px-1.5 py-0.5">時間</div>
        </div>
        <div class="col-span-2 ml-2 text-lg flex flex-wrap gap-x-4 gap-y-2">
          <div class="grid grid-cols-[32px_1fr] grid-rows-[repeat(auto-fill,auto)]">
            <div class="h-[32px] text-sm text-blue-600 m-[1px] rounded-full border border-blue-600 border-solid grid place-items-center">
              土
            </div>
            <div v-for="text in props.data['時間(土曜)'].split(';')" class="col-[2] grid place-items-center ml-1.5 min-h-[32px]">
              {{ text }}
            </div>
          </div>
          <div class="grid grid-cols-[32px_1fr] grid-rows-[repeat(auto-fill,auto)]">
            <div class="h-[32px] text-sm text-red-600 m-[1px] rounded-full border border-red-600 border-solid grid place-items-center">
              日
            </div>
            <div v-for="text in props.data['時間(日曜)'].split(';')" class="col-[2] grid place-items-center ml-1.5 min-h-[32px] ">
              {{ text }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <label class="modal-backdrop" for="my_modal_7">Close</label>
  </div>
</template>
